import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Pagination from './PaginationNew';
import { CardService } from '../services/cardService';
import { RewardsV1Service } from '../services/rewardsV1Service';
import type { RewardV1SearchResult } from '../types/rewardSearchV1';
import type { RewardsV1ListParams, RewardsV1SortBy, RewardsV1SortOrder } from '../types/rewardsV1';
import type { Card } from '../types/card';

function sanitizeInteger(raw: string): string {
  return raw.replace(/[^0-9]/g, '');
}

function formatNumber(value: unknown, opts?: { decimals?: number }): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const decimals = opts?.decimals ?? 2;
  return value.toFixed(decimals).replace(/\.0+$/, '').replace(/(\.[0-9]*?)0+$/, '$1');
}

function getDynamic(row: RewardV1SearchResult) {
  const d = row?.rewards?.dynamic;
  const base_value = typeof d?.base_value === 'number' ? d.base_value : null;
  const base_spend_unit = typeof d?.base_spend_unit === 'number' ? d.base_spend_unit : null;
  const accelerated_value = typeof d?.accelerated_value === 'number' ? d.accelerated_value : null;
  const accelerated_spend_unit = typeof d?.accelerated_spend_unit === 'number' ? d.accelerated_spend_unit : null;

  const acceleration_factor_raw = typeof d?.acceleration_factor === 'number' ? d.acceleration_factor : null;
  const effective_rate_per_inr_raw = typeof d?.effective_rate_per_inr === 'number' ? d.effective_rate_per_inr : null;

  const acceleration_factor =
    acceleration_factor_raw ??
    (accelerated_value != null && base_value != null && base_value > 0 ? accelerated_value / base_value : null);

  const effective_rate_per_inr =
    effective_rate_per_inr_raw ??
    (accelerated_value != null && accelerated_spend_unit != null && accelerated_spend_unit > 0
      ? accelerated_value / accelerated_spend_unit
      : null);

  const limit = typeof row?.rewards?.limit === 'number' ? row.rewards.limit : null;

  return {
    base_value,
    base_spend_unit,
    accelerated_value,
    accelerated_spend_unit,
    acceleration_factor,
    effective_rate_per_inr,
    limit,
  };
}

function computeRpEarned(spend: number, acceleratedValue: number | null, acceleratedSpendUnit: number | null): number | null {
  if (!Number.isFinite(spend) || spend < 0) return null;
  if (acceleratedValue == null || acceleratedSpendUnit == null) return null;
  if (acceleratedValue <= 0 || acceleratedSpendUnit <= 0) return null;
  return (spend * acceleratedValue) / acceleratedSpendUnit;
}

function safeDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function cardLabel(card: Partial<Card> & Record<string, any>): string {
  const bank = String(card.bankName ?? card.bank_name ?? '').trim();
  const name = String(card.name ?? card.cardName ?? card.card_name ?? card.credit_name ?? '').trim();
  const fallbackId = String(card._id ?? card.id ?? '').trim();

  if (bank && name) return `${bank} - ${name}`;
  if (bank && fallbackId) return `${bank} - ${fallbackId}`;
  return bank || name || fallbackId || '';
}

function normalizeCardSearch(raw: string): string {
  const v = raw.trim();
  if (!v) return '';

  // If the user pastes a combined label like "HDFC - Diners Club Black",
  // searching the full string often fails because backend search is simple.
  const parts = v
    .split(/\s*[—–-]\s*/g)
    .map((p) => p.trim())
    .filter(Boolean);

  // Prefer the last segment (usually the card name).
  if (parts.length >= 2 && parts[parts.length - 1].length >= 2) return parts[parts.length - 1];
  return v;
}

const RewardsV1Table: React.FC = () => {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const [cardId, setCardId] = useState('');

  const [cardInput, setCardInput] = useState<string>('');
  const [cardDropdownOpen, setCardDropdownOpen] = useState<boolean>(false);
  const [cardLoading, setCardLoading] = useState<boolean>(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardOptions, setCardOptions] = useState<Card[]>([]);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  const [sortBy, setSortBy] = useState<RewardsV1SortBy>('rate');
  const [sortOrder, setSortOrder] = useState<RewardsV1SortOrder>('desc');

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);

  const [rows, setRows] = useState<RewardV1SearchResult[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [exampleSpend, setExampleSpend] = useState<string>('15000');

  useEffect(() => {
    document.title = page > 1 ? `RewardsV1 - Page ${page}` : 'RewardsV1';
  }, [page]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQ(q), 350);
    return () => window.clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    if (!cardDropdownOpen) return;

    let isMounted = true;
    const handle = window.setTimeout(async () => {
      try {
        setCardLoading(true);
        setCardError(null);

        const search = normalizeCardSearch(cardInput);
        const res = await CardService.getCards({
          page: 1,
          limit: 20,
          ...(search ? { search } : {}),
        });

        if (!isMounted) return;
        setCardOptions(res.data || []);
      } catch (e) {
        if (!isMounted) return;
        setCardError(e instanceof Error ? e.message : 'Failed to load cards');
        setCardOptions([]);
      } finally {
        if (!isMounted) return;
        setCardLoading(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      window.clearTimeout(handle);
    };
  }, [cardDropdownOpen, cardInput]);

  const listParams = useMemo<RewardsV1ListParams>(
    () => ({
      q: debouncedQ.trim() || undefined,
      page,
      limit,
      sortBy,
      sortOrder,
      cardId: cardId.trim() || undefined,
    }),
    [debouncedQ, page, limit, sortBy, sortOrder, cardId]
  );

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await RewardsV1Service.list(listParams);
        if (!isMounted) return;

        setRows(res.data || []);
        setTotalPages(res.pages || 1);
        setTotalItems(typeof res.total === 'number' ? res.total : (res.data || []).length);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load RewardsV1');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [listParams]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rewards</h1>
          {/* <p className="mt-1 text-sm text-gray-500">Backed by GET /api/rewardsv1 (paginated, sortable).</p> */}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Search (q)</label>
            <input
              type="text"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="e.g. flight"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Credit card (search & select)</label>
            <div className="relative mt-1">
              <input
                value={cardInput}
                onChange={(e) => {
                  setSelectedCard(null);
                  setCardId('');
                  setCardInput(e.target.value);
                  setCardDropdownOpen(true);
                  setPage(1);
                }}
                onFocus={() => setCardDropdownOpen(true)}
                onBlur={() => {
                  // allow click selection from dropdown
                  setTimeout(() => setCardDropdownOpen(false), 150);
                }}
                placeholder="Type bank or card name (e.g., hdfc)"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />

              {cardDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
                  {cardLoading && <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>}

                  {cardError && !cardLoading && <div className="px-3 py-2 text-sm text-red-700">{cardError}</div>}

                  {!cardLoading && !cardError && cardOptions.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">No cards found.</div>
                  )}

                  {!cardLoading && !cardError && cardOptions.length > 0 && (
                    <div className="max-h-64 overflow-auto">
                      {cardOptions.map((c) => {
                        const key = c._id || c.id;
                        const chosen = selectedCard?._id && c._id && selectedCard._id === c._id;
                        return (
                          <button
                            key={key}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setSelectedCard(c);
                              setCardInput(cardLabel(c));
                              setCardDropdownOpen(false);
                              setCardId(c._id || c.id || '');
                              setPage(1);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${chosen ? 'bg-gray-50' : ''}`}
                          >
                            <div className="text-gray-900">
                              {cardLabel(c)}
                            </div>
                            <div className="text-xs text-gray-500 break-all">{c._id || c.id}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {selectedCard ? (
              <div className="mt-2 text-xs text-gray-500">Selected: {cardLabel(selectedCard)}</div>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Sort</label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as RewardsV1SortBy);
                setPage(1);
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="rate">rate</option>
              <option value="created">created</option>
              <option value="updated">updated</option>
              <option value="date">date</option>
              <option value="search">search</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Order</label>
            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as RewardsV1SortOrder);
                setPage(1);
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="desc">desc</option>
              <option value="asc">asc</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              setQ('');
              setDebouncedQ('');
              setCardId('');
              setSelectedCard(null);
              setCardInput('');
              setCardDropdownOpen(false);
              setSortBy('rate');
              setSortOrder('desc');
              setPage(1);
              setLimit(50);
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Clear
          </button>

          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-500">Spend for calc:</div>
            <input
              value={exampleSpend}
              inputMode="numeric"
              onChange={(e) => setExampleSpend(sanitizeInteger(e.target.value))}
              className="w-28 border border-gray-300 rounded-md px-2 py-1 text-sm"
              placeholder="15000"
              title="Used to compute RP Earned / Capped RP"
            />
          </div>
          <div className="text-sm text-gray-500">Total: {totalItems}</div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Values</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calculation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && !rows.length ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                    <div className="mt-2">Loading RewardsV1…</div>
                  </td>
                </tr>
              ) : !rows.length ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                    No results.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r._id}
                    className={`${
                      String(r.status || '').toLowerCase() === 'inactive'
                        ? 'bg-red-50 hover:bg-red-100'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">{r.name || '—'}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <span>{r.bank_name || '—'} • {r.card_name || '—'}</span>
                        {String(r.status || '').toLowerCase() === 'inactive' ? (
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">
                            INACTIVE
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 text-xs text-gray-600">{r._id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.categoryprimary || '—'}</td>
                    <td className="px-6 py-4 text-xs text-gray-900">
                      {(() => {
                        const d = getDynamic(r);
                        return (
                          <div className="space-y-0.5">
                            <div>
                              <span className="text-gray-500">Base:</span>{' '}
                              <span className="font-mono">{formatNumber(d.base_value, { decimals: 0 })}</span>
                              <span className="text-gray-400"> / </span>
                              <span className="font-mono">{formatNumber(d.base_spend_unit, { decimals: 0 })}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Accel:</span>{' '}
                              <span className="font-mono">{formatNumber(d.accelerated_value, { decimals: 0 })}</span>
                              <span className="text-gray-400"> / </span>
                              <span className="font-mono">{formatNumber(d.accelerated_spend_unit, { decimals: 0 })}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Limit:</span>{' '}
                              <span className="font-mono">{formatNumber(d.limit, { decimals: 0 })}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-900">
                      {(() => {
                        const d = getDynamic(r);
                        const spend = Number(exampleSpend);
                        const rp = computeRpEarned(spend, d.accelerated_value, d.accelerated_spend_unit);
                        const capped =
                          rp == null
                            ? null
                            : d.limit != null && d.limit > 0
                              ? Math.min(rp, d.limit)
                              : rp;

                        return (
                          <div className="space-y-0.5">
                            <div>
                              <span className="text-gray-500">Return:</span>{' '}
                              <span className="font-mono">
                                {d.acceleration_factor == null
                                  ? '—'
                                  : `${formatNumber(d.acceleration_factor, { decimals: 4 })}X`}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Rate/INR:</span>{' '}
                              <span className="font-mono">{formatNumber(d.effective_rate_per_inr, { decimals: 6 })}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Rate(%):</span>{' '}
                              <span className="font-mono">
                                {d.effective_rate_per_inr == null
                                  ? '—'
                                  : `${formatNumber(d.effective_rate_per_inr * 100, { decimals: 2 })}%`}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">RP@{exampleSpend || '—'}:</span>{' '}
                              <span className="font-mono">{rp == null ? '—' : formatNumber(rp, { decimals: 2 })}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Capped:</span>{' '}
                              <span className="font-mono">
                                {capped == null ? '—' : formatNumber(capped, { decimals: 2 })}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{safeDate((r as any).updatedAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-3">
                        <Link
                          to={`/rewards/json-edit/${encodeURIComponent(r._id)}`}
                          className="inline-flex items-center justify-center rounded-md border border-blue-200 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"
                          title="Json"
                          aria-label="Json"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <path d="M14 2v6h6" />
                            <path d="M8 13h8" />
                            <path d="M8 17h8" />
                            <path d="M8 9h4" />
                          </svg>
                        </Link>
                        <Link
                          to={`/rewards/edit/${encodeURIComponent(r._id)}`}
                          className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50"
                          title="Edit"
                          aria-label="Edit"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                          </svg>
                        </Link>
                        <Link
                          to={`/rewards/new/${encodeURIComponent(r._id)}`}
                          className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50"
                          title="Duplicate"
                          aria-label="Duplicate"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <rect x="2" y="2" width="13" height="13" rx="2" ry="2" />
                          </svg>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={limit}
        onPageChange={(next) => setPage(next)}
        onLimitChange={(nextLimit) => {
          setLimit(nextLimit);
          setPage(1);
        }}
      />
    </div>
  );
};

export default RewardsV1Table;
